/** Small shared pieces: stat tiles, rank cells, score badges, filter chips. */
import type { ReactNode } from 'react'
import type { MetricDef, Standing } from '@/data/selectors'
import { rankShort, rankState, rankText } from '@/measures/rankText'
import { lowVolumeReason, unscoredReason, type LowVolume } from '@/measures/scorecard'
import { MINIMUM_VOLUME_LABEL } from '@/measures/minimumVolume'
import { ABSENT, type GrainKey } from '@/data/coverage'
import { money0, money2, int, pct, dec, dateUs } from '@/format'
import type { Definition } from '@/measures/definitions'
import type { Team } from '@/measures/teams'
import { MODELED_FILTER_NOTE } from './MultiSelect'
import { Info } from './Info'

export { Info }

export function fmt(format: MetricDef['format'], v: number | null): string {
  switch (format) {
    case 'money2':
      return money2(v)
    case 'money0':
      return money0(v)
    case 'pct':
      return pct(v)
    case 'dec':
      return dec(v)
    default:
      return int(v)
  }
}

/* ------------------------------------------------------------- absent grains */

/**
 * The panel a page shows when the grain it is built on is not in the bundle.
 *
 * Says what is missing and why, in that order, and never in the vocabulary of failure —
 * this is a pipeline that has not produced a table yet, not an error.
 */
export function NotLoaded({
  grain,
  children,
}: {
  grain: GrainKey
  /** Anything extra worth saying at this particular call site. */
  children?: ReactNode
}) {
  const { what, why } = ABSENT[grain]
  return (
    <div className="notice" role="note">
      <span className="eyebrow">Not in this bundle</span>
      <p className="notice-what">{what}</p>
      <p className="notice-why">{why}</p>
      {children}
    </div>
  )
}

/**
 * A single figure that cannot be computed because its grain is absent.
 *
 * Renders the same em-dash `format` already produces for a null, plus the reason on
 * hover — so a reader who wonders about one dash does not have to find the page that
 * explains it.
 */
export function Absent({ grain, short }: { grain: GrainKey; short?: string }) {
  return (
    <span className="absent" title={`${ABSENT[grain].what} ${ABSENT[grain].why}`}>
      {short ?? '—'}
    </span>
  )
}

/** One cell of the stat strip. The strip is ONE bordered object, not many cards. */
export function Stat({
  label,
  value,
  note,
  hero = false,
  modeled = false,
  absent,
  help,
}: {
  label: string
  value: string
  note?: ReactNode
  hero?: boolean
  modeled?: boolean
  /** The grain this figure needs, when the bundle does not carry it. */
  absent?: GrainKey
  /** What the measure means, shown on hover. See measures/definitions.ts. */
  help?: Definition
}) {
  return (
    <div className={hero ? 'stat-cell is-hero' : 'stat-cell'}>
      <span className="eyebrow">
        {label}
        <Info def={help} />
        {modeled && (
          <span className="modeled" title="Modelled, not observed">
            M
          </span>
        )}
      </span>
      <span
        className={hero ? 'hero-figure' : 'stat-figure'}
        style={absent ? { color: 'var(--ink-3)' } : undefined}
      >
        {absent ? <Absent grain={absent} /> : value}
      </span>
      {/* The reason replaces the note rather than sitting beside it: a tile whose figure
          is absent has nothing to annotate. */}
      {absent ? (
        <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-3)' }}>
          not in this bundle
        </span>
      ) : (
        note && <span style={{ fontSize: 'var(--t-rank)', color: 'var(--ink-3)' }}>{note}</span>
      )}
    </div>
  )
}

export function StatStrip({ children, cols }: { children: ReactNode; cols: number }) {
  // `cols` is the widest the strip should ever get; below that it wraps rather than
  // squeezing six tiles into a laptop width.
  return (
    <div
      className="stat-strip"
      style={{
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${Math.max(
          148,
          Math.floor(1100 / cols),
        )}px), 1fr))`,
      }}
    >
      {children}
    </div>
  )
}

/**
 * Name over a muted second line, in every table that lists people.
 *
 * Both lines clamp to one line each. Real names and real department strings vary from
 * "Roth, Eli" to "Medicaid Collections Supervisor · Medicaid - Signet · Off-shore", and
 * letting them wrap gave one table rows of three different heights.
 */
export function PersonCell({ name, meta }: { name: string; meta?: ReactNode }) {
  return (
    <div className="person-cell">
      <div className="person-name" title={name}>
        {name}
      </div>
      {meta !== undefined && meta !== null && meta !== '' && (
        <div className="person-meta" title={typeof meta === 'string' ? meta : undefined}>
          {meta}
        </div>
      )}
    </div>
  )
}

/**
 * The value with its rank underneath. No bars — they only restated the rank.
 * The rank carries the colour; the value stays in ink either way.
 */
/**
 * A team head's own job title, as the small grey line under their name — the
 * Ledger's person cell. Asked for in the 2026-09-25 review: a card said who led a
 * team but not what they were.
 */
export function SupervisorTitle({ t }: { t: Team }) {
  return t.ownTitle ? (
    <span className="sub2">{t.ownTitle}</span>
  ) : (
    <span className="sub2" title="No job title for this supervisor in the data">
      —
    </span>
  )
}

/**
 * A figure with its rank in the person's own job title under it. The comparison group
 * is said on hover of the cell itself — "Rank 5 of 28 HMO Billing Specialists in
 * Jan–Aug 2026" — not in a note elsewhere on the page (Shayna, 2026-10-02).
 */
export function RankCell({
  standing,
  format,
  label,
  period,
}: {
  standing: Standing
  format: MetricDef['format']
  /** The measure's name, leading the hover. */
  label: string
  /** The months the rank covers, e.g. "Jan–Aug 2026". */
  period: string
}) {
  const cls =
    standing.band === 'good'
      ? 'rank rank--good'
      : standing.band === 'crit'
        ? 'rank rank--crit'
        : 'rank rank--mid'
  const v = fmt(format, standing.value)
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
      title={`${label}: ${v} · ${rankText(standing, period)}`}
    >
      <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
        {v}
      </span>
      <span className={rankState(standing) === 'ranked' ? cls : 'rank'}>{rankShort(standing)}</span>
    </div>
  )
}

/**
 * The red "terminated" chip beside a name, with the date (review #605: "can we add the
 * term date near terminated when flagged?"). Paylocity's termination date, off
 * DimEmployee; a person without one keeps the bare word.
 */
export function TerminatedChip({ date }: { date: string | null }) {
  return (
    <span
      className="pts-chip crit"
      data-testid="terminated-chip"
      title={date ? `Terminated ${dateUs(date)}, per Paylocity` : 'Terminated, per Paylocity'}
    >
      terminated{date ? ` ${dateUs(date)}` : ''}
    </span>
  )
}

/**
 * Beside a score of 0 that is the low-volume override (review #695): says so, and what
 * the score would have been, on hover.
 */
export function LowVolumeTag({ lowVolume }: { lowVolume: LowVolume | undefined }) {
  if (!lowVolume) return null
  return (
    <span className="pts-chip" data-testid="low-volume-tag" title={lowVolumeReason(lowVolume)} style={{ marginLeft: 4 }}>
      {MINIMUM_VOLUME_LABEL}
    </span>
  )
}

/** Scorecard badge. Status fill plus a 1px border, per the design system. */
export function ScoreBadge({
  total,
  scored,
  terminated = false,
  lowVolume,
}: {
  total: number
  scored: boolean
  /** Unscored because they have left, not for want of claims. */
  terminated?: boolean
  /** Scored 0, below the minimum billing volume: the tag beside it says so. */
  lowVolume?: LowVolume
}) {
  if (!scored) {
    return (
      <span
        className="badge"
        style={{ color: 'var(--ink-3)' }}
        title={unscoredReason({ terminated })}
      >
        —
      </span>
    )
  }
  const cls = total >= 7 ? 'badge badge--good' : total <= 0 ? 'badge badge--crit' : 'badge'
  return (
    <>
      <span className={cls}>{total > 0 ? `+${total}` : total}</span>
      <LowVolumeTag lowVolume={lowVolume} />
    </>
  )
}

/** Scope pill. */
export function Pill({ children, onClear }: { children: ReactNode; onClear?: () => void }) {
  return (
    <span className="pill">
      {children}
      {onClear && (
        <button
          onClick={onClear}
          aria-label="Clear"
          style={{
            marginLeft: 6,
            background: 'none',
            border: 0,
            cursor: 'pointer',
            color: 'inherit',
            padding: 0,
            fontSize: 'var(--t-table)',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </span>
  )
}

/** Callout card with a left accent rule: one figure, one sentence. */
export function Callout({
  figure,
  children,
  tone = 'accent',
}: {
  figure: string
  children: ReactNode
  tone?: 'accent' | 'crit' | 'good'
}) {
  const colour =
    tone === 'crit' ? 'var(--crit)' : tone === 'good' ? 'var(--good)' : 'var(--accent)'
  return (
    <div
      className="card"
      style={{ borderLeft: `3px solid ${colour}`, display: 'grid', gap: 8 }}
    >
      <span className="stat-figure" style={{ color: 'var(--ink)' }}>
        {figure}
      </span>
      <p className="body-note" style={{ margin: 0 }}>
        {children}
      </p>
    </div>
  )
}

/** Labelled dropdown for the filter bar. A disabled one keeps its reason on hover. */
export function Select({
  label,
  value,
  options,
  onChange,
  disabled,
  why,
  reason = 'empty',
  lead = false,
  modeled = false,
  hint,
  title,
}: {
  label: string
  value?: string
  options?: { value: string; label: string }[]
  onChange?: (v: string) => void
  disabled?: boolean
  why?: string
  /**
   * Why a disabled control is disabled. 'empty' — the column carries nothing worth
   * filtering on. 'unbuilt' — it would work, the pipeline has not carried it yet.
   * The placeholder says which, because they are not the same news.
   */
  reason?: 'empty' | 'unbuilt'
  lead?: boolean
  /** Marks a filter whose result is an allocation rather than a count. */
  modeled?: boolean
  /** A muted qualifier after the label — "Payroll range *period*". */
  hint?: string
  /** Tooltip on the whole control, for a label that needs a sentence. */
  title?: string
}) {
  // A disabled control says why in the control itself, per Shayna — an empty greyed
  // box reads as broken; "Not in the data" reads as a decision.
  const shown = disabled
    ? [
        {
          value: '',
          label: reason === 'unbuilt' ? 'Not in this bundle' : 'Nothing to filter on',
        },
      ]
    : options

  /*
   * No blanket opacity on a disabled control.
   *
   * `opacity: 0.6` on the label composited BOTH lines against the filter bar's ground
   * and dropped them under 4.5:1 — worst in dark mode, where the fill is already close
   * to the text. And because opacity lives on the ancestor, a per-element contrast check
   * reads the child as fully opaque and never sees it.
   *
   * The disabled state is carried instead by the things that actually say "disabled":
   * a softer rule, italic placeholder text, and the reason on hover. The muted tone is
   * --ink-3, which is picked to clear AA on every ground it sits on.
   */
  return (
    <label title={disabled ? why : title} style={{ display: 'grid', gap: 4 }}>
      <span
        className="eyebrow"
        style={{
          color: lead ? 'var(--accent-ink)' : disabled ? 'var(--ink-3)' : undefined,
        }}
      >
        {label}
        {hint && <span style={{ color: 'var(--ink-3)' }}> {hint}</span>}
        {modeled && (
          <span
            className="modeled"
            style={{ marginLeft: 6, verticalAlign: 0 }}
            title={MODELED_FILTER_NOTE}
          >
            M
          </span>
        )}
      </span>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 'var(--t-table)',
          padding: '5px 7px',
          borderRadius: 6,
          // A lead control — the one that changes what the chart IS — is filled in the
          // soft accent with a heavier accent border, as the Ledger's primary picker.
          border: `${lead ? '1.5px' : '1px'} ${disabled ? 'dashed' : 'solid'} ${
            lead ? 'var(--accent)' : 'var(--rule-2)'
          }`,
          // Sits on the wells tone rather than the surface, so it reads as inset and
          // unavailable without having to be faded out.
          background: disabled ? 'var(--surface-2)' : lead ? 'var(--accent-soft)' : 'var(--surface)',
          color: disabled ? 'var(--ink-3)' : lead ? 'var(--accent-ink)' : 'var(--ink)',
          fontWeight: lead ? 500 : undefined,
          fontStyle: disabled ? 'italic' : 'normal',
          cursor: disabled ? 'not-allowed' : undefined,
          // Chrome fades a disabled <select> to 0.7 in its UA stylesheet, which drops
          // this text to 2.8:1. The placeholder is the only thing saying WHY the
          // control is dead, so it is exactly the text that has to stay readable.
          opacity: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        {shown?.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}
